import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { mockBillingAffordancesEnabled } from '../../../shared/commercialActions';
import { mockSubscriptionGateway } from '../index';
import { notifySubscriptionChanged } from '../subscriptionChanged';
import { formatCents } from './money';
import '../billing.css';

/** Honest label for the provider-less sandbox. Renders nothing in a production bundle. */
export function MockBillingNotice() {
  if (!mockBillingAffordancesEnabled) return null;
  return (
    <p className="billing-notice billing-notice--mock" role="note" data-testid="mock-billing-notice">
      <strong>Mock billing sandbox.</strong> Payments, invoices and plan changes on this screen are simulated for demos and
      tests. No real payment is made and no payment provider is contacted.
    </p>
  );
}

/** Demo-only buttons that make the mock server report a failed payment, an ended grace period and so on. They exist so
 * every state can be shown without a payment provider. Renders nothing in a production bundle. */
export function MockBillingDemoControls({ onChanged }: { onChanged: () => void }) {
  const [message, setMessage] = useState<string | null>(null);
  if (!mockBillingAffordancesEnabled || !mockSubscriptionGateway) return null;
  const gateway = mockSubscriptionGateway;

  function run(label: string, action: () => void) {
    action();
    notifySubscriptionChanged();
    onChanged();
    setMessage(`${label} (mock).`);
  }

  return (
    <details className="billing-mock-controls" data-testid="mock-demo-controls">
      <summary>Mock sandbox controls (demo only)</summary>
      <p className="muted">These make the mock server behave as a real provider would. They are not available in production.</p>
      <div className="inline-form">
        <button type="button" onClick={() => run('Payment failure simulated', () => gateway.simulatePaymentFailure())}>Simulate a failed payment</button>
        <button type="button" onClick={() => run('Grace period ended', () => gateway.simulateGraceExpired())}>Simulate the grace period ending</button>
        <button type="button" onClick={() => run('Trial ended', () => gateway.simulateTrialExpired())}>Simulate the trial ending</button>
        <button type="button" onClick={() => run('Billing period ended', () => gateway.simulatePeriodEnd())}>Simulate a scheduled cancellation taking effect</button>
      </div>
      {message && <p role="status" className="muted">{message}</p>}
    </details>
  );
}

/** `/settings/billing/mock-checkout` — stands in for the provider-hosted Checkout page in the mock sandbox. */
export function MockCheckoutPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session') ?? '';
  const [declined, setDeclined] = useState(false);

  if (!mockBillingAffordancesEnabled || !mockSubscriptionGateway) return null;
  const gateway = mockSubscriptionGateway;
  const summary = gateway.describeMockCheckout(sessionId);

  if (!summary) {
    return (
      <section className="card card--wide">
        <MockBillingNotice />
        <h2>Mock checkout</h2>
        <p>This mock checkout session was not found. It may have been created before the page was reloaded.</p>
        <Link to="/settings/billing">Back to billing</Link>
      </section>
    );
  }

  return (
    <section className="card card--wide" aria-labelledby="mock-checkout-heading">
      <MockBillingNotice />
      <h2 id="mock-checkout-heading">Mock secure checkout</h2>
      <p>This page stands in for the provider&apos;s hosted payment page.</p>
      <table className="billing-table" aria-label="Checkout summary">
        <tbody>
          <tr><th scope="row">{summary.planName} (first month, estimate)</th><td className="billing-table__amount">{formatCents(summary.subtotalCents)}</td></tr>
          <tr><th scope="row">Tax</th><td className="billing-table__amount">{formatCents(summary.taxCents)}</td></tr>
          <tr className="billing-table__total"><th scope="row">Total due today</th><td className="billing-table__amount">{formatCents(summary.totalCents)}</td></tr>
        </tbody>
      </table>
      {declined && <p className="error" role="alert">Your card was declined (mock). Try again or go back.</p>}
      <div className="inline-form">
        <button type="button" onClick={() => { gateway.completeMockCheckout(sessionId, 'paid'); navigate('/settings/billing/checkout-complete'); }}>
          Pay with test card 4242
        </button>
        <button type="button" className="billing-button--secondary" onClick={() => { gateway.completeMockCheckout(sessionId, 'declined'); setDeclined(true); }}>
          Use a card that is declined
        </button>
        <button type="button" className="link" onClick={() => { gateway.completeMockCheckout(sessionId, 'canceled'); navigate('/settings/billing'); }}>
          Cancel and go back
        </button>
      </div>
    </section>
  );
}

/** `/settings/billing/mock-portal` — stands in for the provider's customer portal in the mock sandbox. */
export function MockPortalPage() {
  const navigate = useNavigate();
  if (!mockBillingAffordancesEnabled || !mockSubscriptionGateway) return null;
  const gateway = mockSubscriptionGateway;

  return (
    <section className="card card--wide" aria-labelledby="mock-portal-heading">
      <MockBillingNotice />
      <h2 id="mock-portal-heading">Mock customer portal</h2>
      <p>This page stands in for the provider&apos;s secure page for updating a payment method.</p>
      <div className="inline-form">
        <button type="button" onClick={() => { gateway.completeMockPortalUpdate(); notifySubscriptionChanged(); navigate('/settings/billing'); }}>
          Use a new test card (Mastercard ending 4444)
        </button>
        <button type="button" className="link" onClick={() => navigate('/settings/billing')}>Back to billing</button>
      </div>
    </section>
  );
}
